import {MouseEvent} from 'react';
import {LazyImage, Rating, Text, Inline} from '@treatwell/ui';
import i18next from 'i18next';
import styles from './VenueCard.module.css';
import {formatDistance, formatDistanceUnit} from '../../../utils/formatters';
import {AvailabilityApiResponseChannel} from '../../../../server/routes/availability/types';
import {BaseVenueOutput} from './types';
import {calculateDistance} from '../../../utils/calculateDistance';
import {SDKEventAttrs} from '@mparticle/web-sdk';
import {trackEvent} from '../../../utils/mParticleTracking';

type Props = BaseVenueOutput & {
  channelData: AvailabilityApiResponseChannel;
  venueLocationPoints: {
    lat?: number;
    lon?: number;
  };
  eventAttributes: SDKEventAttrs;
};

export const VenueCard = ({
  id,
  uri,
  primaryImage,
  name,
  rating,
  location,
  channelData,
  venueLocationPoints,
  eventAttributes,
}: Props) => {
  const titleId = `title_${id}`;
  const redirectUri = uri.desktopUri;
  const imgUri = primaryImage.uris['360x240'];
  const resultVenueLocation = location.point;
  const ratingCount = rating.count || 0;
  const ratingAverage = rating.average || 0;
  let distanceAway = null;
  if (
    typeof venueLocationPoints.lat === 'number' &&
    typeof venueLocationPoints.lon === 'number'
  ) {
    distanceAway = calculateDistance(
      {lat: venueLocationPoints.lat, lon: venueLocationPoints.lon},
      {lat: resultVenueLocation.lat, lon: resultVenueLocation.lon},
      channelData.distanceUnit,
    );
  }

  if (distanceAway) {
    eventAttributes.venue_distance = distanceAway;
  }

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    trackEvent({eventName: 'available_salon_clicked', eventAttributes});

    if (redirectUri) {
      window.location.href = redirectUri;
    }
  };
  return (
    <div
      className={styles.root}
      role="group"
      aria-labelledby={titleId}
      key={id}
    >
      <a
        href={redirectUri}
        className={styles.venueCardLink}
        onClick={handleClick}
        aria-labelledby={titleId}
        data-cy="VenueCardLink"
      />
      <LazyImage className={styles.image} alt="" src={imgUri} />
      <div className={styles.contentContainer}>
        <Text type="bodyHeavy" id={titleId}>
          {name}
        </Text>
        {distanceAway && (
          <Text type="caption">
            {i18next
              .t('salonSuggestions.distance', {
                count: Number(formatDistance(distanceAway, channelData)),
                unit: formatDistanceUnit(channelData.distanceUnit),
              })
              .toString()}
          </Text>
        )}
        <Inline space="md">
          <Rating
            rating={ratingAverage}
            size={16}
            ariaLabel={
              ratingCount === 0
                ? i18next.t('salonSuggestions.a11y.ratingNoReviews')
                : i18next.t('salonSuggestions.a11y.rating', {
                    rating: ratingAverage,
                    count: ratingCount,
                  })
            }
            showRatingValue
          />
          <Text type="footnote" aria-hidden="true">
            ({ratingCount})
          </Text>
        </Inline>
      </div>
    </div>
  );
};
